import { appApiSlice } from "src/api/app-ApiSlice";
import {
    TAG_BILLING_DATA,
    TAG_PARTICIPANT_LISTS,
    TAG_PROFILE_SETTINGS,
    TAG_PROJECT_QUOTE_DATA,
    TAG_USER_INFO,
} from "src/api/api-tags";
import {
    ApiResponse,
    ChangePasswordRequest,
    GetParticipantListsResponse,
    GetProfileSettingsResponse,
    ProfileImageType,
    SaveProfileSettingsRequest,
    SaveWorkHourPricesRequest,
    SubmitFeedbackSurveyRequest,
    UploadProfileImageResponse,
} from "src/model/api/v2/data-contracts";

export const profileSettingsV2ApiSlice = appApiSlice.injectEndpoints({
    endpoints: builder => ({
        getProfileSettings: builder.query<GetProfileSettingsResponse, void>({
            query: () => 'v2/profile-settings/get',
            providesTags: [TAG_PROFILE_SETTINGS],
            keepUnusedDataFor: 0,
        }),
        // the page keeps the edited form as its own state, so a save doesn't refetch the profile
        saveProfileSettings: builder.mutation<ApiResponse, SaveProfileSettingsRequest>({
            query: (body) => ({
                url: 'v2/profile-settings/save',
                method: 'POST',
                body,
            }),
            // the quote shows the company details
            invalidatesTags: [TAG_USER_INFO, TAG_BILLING_DATA, TAG_PROJECT_QUOTE_DATA],
        }),
        uploadProfileImage: builder.mutation<UploadProfileImageResponse, { type: ProfileImageType, image: File }>({
            query: ({ type, image }) => {
                const body = new FormData();
                body.append('image', image);
                return {
                    url: `v2/profile-settings/upload-image?type=${type}`,
                    method: 'POST',
                    body,
                };
            },
            // the avatar is shown in the header, the logo on the quote
            invalidatesTags: (_result, _error, { type }) =>
                type === ProfileImageType.Avatar ? [TAG_USER_INFO] : [TAG_PROJECT_QUOTE_DATA],
        }),
        saveWorkHourPrices: builder.mutation<ApiResponse, SaveWorkHourPricesRequest>({
            query: (body) => ({
                url: 'v2/profile-settings/save-work-hour-prices',
                method: 'POST',
                body,
            }),
            invalidatesTags: [TAG_PROFILE_SETTINGS],
        }),
        changePassword: builder.mutation<ApiResponse, ChangePasswordRequest>({
            query: (body) => ({
                url: 'v2/profile-settings/change-password',
                method: 'POST',
                body,
            }),
        }),
        requestAccountDeletion: builder.mutation<ApiResponse, void>({
            query: () => ({
                url: 'v2/profile-settings/request-account-deletion',
                method: 'POST',
            }),
        }),
        submitFeedbackSurvey: builder.mutation<ApiResponse, SubmitFeedbackSurveyRequest>({
            query: (body) => ({
                url: 'v2/profile-settings/submit-feedback-survey',
                method: 'POST',
                body,
            }),
        }),
        getParticipantLists: builder.query<GetParticipantListsResponse, void>({
            query: () => 'v2/profile-settings/get-participant-lists',
            providesTags: [TAG_PARTICIPANT_LISTS],
            keepUnusedDataFor: 0,
        }),
    }),
});

export const {
    useGetProfileSettingsQuery,
    useSaveProfileSettingsMutation,
    useUploadProfileImageMutation,
    useSaveWorkHourPricesMutation,
    useChangePasswordMutation,
    useRequestAccountDeletionMutation,
    useSubmitFeedbackSurveyMutation,
    useGetParticipantListsQuery,
} = profileSettingsV2ApiSlice;
